import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter, Routes, Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './components/App';
import './utils/auditLog'; // Initialize audit logging (sets up window.pletAudit)
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Verify from './pages/auth/Verify';
import ForgotPassword from './pages/auth/ForgotPassword';
import AcceptInvite from './pages/AcceptInvite';
import PublicHome from './pages/PublicHome';
import AuthProvider, { useAuth } from './components/AuthProvider';
import WindowControls from './components/ui/WindowControls';
import { SubscriptionProvider } from './context/SubscriptionContext';
import reportWebVitals from './reportWebVitals';

// Dev-only perf harness (no auth) — excluded from production bundles
const DevDocEditorPerf =
  process.env.NODE_ENV === 'development'
    ? React.lazy(() => import('./pages/dev/DocEditorPerf'))
    : null;

// Detect if running in Electron PRODUCTION (file:// protocol only)
// In Electron dev mode, we use localhost so BrowserRouter works fine
const isElectronProduction = typeof window !== 'undefined' && window.location.protocol === 'file:';

// Add electron class to body for CSS-based electron detection (e.g. drag regions on auth pages)
if (window.electronAPI?.isElectron) {
  document.body.classList.add('electron-app');
}

// Use HashRouter ONLY for Electron production builds (file:// protocol)
// BrowserRouter works fine for Electron dev mode (localhost) and web deployment
const Router = isElectronProduction ? HashRouter : BrowserRouter;

// Sentry DSNs for each environment (separate Sentry projects)
const SENTRY_DSN = {
  staging: 'https://966eceb937e59e0df2e2374d264bff54@o4510829870514176.ingest.de.sentry.io/4510830120730704',
  production: 'https://2a327ba67a843ffec3d84cc122774b94@o4510829870514176.ingest.de.sentry.io/4510830125056080',
};

// Determine environment based on Amplify's REACT_APP_ENV or fallback to hostname detection
const getSentryEnvironment = () => {
  if (process.env.REACT_APP_ENV) return process.env.REACT_APP_ENV;
  if (typeof window !== 'undefined') {
    if (window.location.hostname.includes('staging')) return 'staging';
    if (window.location.hostname === 'fokus.plet.ai') return 'production';
  }
  return 'development';
};

const sentryEnv = getSentryEnvironment();

// Initialize Sentry for error tracking and performance monitoring
Sentry.init({
  dsn: SENTRY_DSN[sentryEnv] || null, // null disables Sentry in development
  environment: sentryEnv,
  sendDefaultPii: true, // Collect IP addresses for user context
  integrations: [
    Sentry.browserTracingIntegration(),
    // Session Replay records the DOM continuously so error-replays have a
    // buffer to flush — serializing a textarea's ENTIRE value on every input
    // event (measurable typing jank in large code blocks). Local dev often
    // points REACT_APP_ENV at staging, which used to switch recording ON for
    // every dev session — gate on the ACTUAL build instead. In deployed
    // builds, `ignore` skips per-keystroke capture in code block textareas
    // (content still appears via periodic full snapshots).
    ...(process.env.NODE_ENV !== 'development'
      ? [Sentry.replayIntegration({ ignore: ['.code-block-textarea'] })]
      : []),
    // Capture console.error as Sentry events (for React DOM warnings like invalid SVG attributes)
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],
  // Performance Monitoring - lower sample rate in production to reduce costs
  tracesSampleRate: sentryEnv === 'production' ? 0.1 : 1.0,
  /**
   * Session Replay uploads are noisy (mousemove/DOM mutations) and can hit ingest 429s if rates are high.
   * Staging previously used 50% session replay → rate limits; keep error-triggered replay at full sample.
   */
  replaysSessionSampleRate: sentryEnv === 'production' ? 0.05 : 0,
  replaysOnErrorSampleRate: 1.0,
  // Enable in staging and production (not in local development unless explicitly enabled)
  enabled: sentryEnv !== 'development' || process.env.REACT_APP_SENTRY_ENABLED === 'true',
});

/**
 * Wait for auth before the app. Room walls render inside the app so the
 * sidebar stays visible.
 */
const SubscriptionGate = ({ children }) => {
  const { loading: authLoading, authenticated } = useAuth();

  if (authLoading) {
    return null;
  }

  if (!authenticated) {
    return <PublicHome />;
  }

  return children;
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ Error caught by boundary:', error, errorInfo);
    this.setState({ error, errorInfo });
    // Report error to Sentry
    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2a5298 100%)',
          color: '#fff',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '600px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
            <h1 style={{ margin: '0 0 20px 0', fontSize: '32px' }}>Something went wrong</h1>
            <p style={{ margin: '0 0 30px 0', fontSize: '16px', lineHeight: '1.6', opacity: 0.9 }}>
              An error was encountered with the requested page.
            </p>
            <details style={{ textAlign: 'left', marginBottom: '20px', fontSize: '14px' }}>
              <summary style={{ cursor: 'pointer', marginBottom: '10px' }}>Error Details</summary>
              <pre style={{ 
                background: 'rgba(0,0,0,0.3)', 
                padding: '15px', 
                borderRadius: '8px', 
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                {this.state.error && this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
            <button
              onClick={() => window.location.protocol === 'file:' ? window.location.hash = '/' : window.location.href = '/'}
              style={{
                background: 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
                color: '#fff',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 15px rgba(0,217,255,0.3)',
                marginRight: '10px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0,217,255,0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(0,217,255,0.3)';
              }}
            >
              Return to Home
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'rgba(255,255,255,0.1)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <WindowControls floating />
      <Router>
        <Routes>
          {/* Dev-only perf harness */}
          {DevDocEditorPerf && (
            <Route
              path="/dev/perf/doc-editor"
              element={
                <React.Suspense fallback={null}>
                  <DevDocEditorPerf />
                </React.Suspense>
              }
            />
          )}

          {/* Auth routes - no AuthProvider wrapper */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/verify" element={<Verify />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          
          {/* Protected app routes */}
          <Route path="/*" element={
            <AuthProvider>
              <SubscriptionProvider>
                <SubscriptionGate>
                  <App />
                </SubscriptionGate>
              </SubscriptionProvider>
            </AuthProvider>
          } />
        </Routes>
      </Router>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
